import {Timer} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {AutoplayView} from '@gongxigames/framework/slot/modules/autoplay/AutoplayView';
import {AutoplayModule} from '@gongxigames/framework/slot/modules/autoplay/AutoplayModule';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {RealityCheckEvents} from '@gongxigames/framework/slot/modules/realitycheck';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {ModuleState} from '@gongxigames/framework/slot/fragments/module/ModuleState';

export class AutoplayWaitingForReadinessState extends ModuleState<AutoplayView, AutoplayModule, GlobalEventsMapForBackwardCompatibility> {

    protected _isWait = false;
    protected _wonBigWin = false;
    protected _bigWinFinished = false;
    protected _isNoWin = false;
    protected _initialWinAnimationFinished = false;
    protected _allPayAnimationsFinished = false;
    protected _winMeterFinished = false;

    public onEnterState(): void {
        this.view.showAutoplayUI();

        if (this.module.roundDelay > 0) {
            this._isWait = true;
            this.module.updateManager.add(new Timer(this.onCompleteRoundDelay.bind(this), this.module.roundDelay, true));
        }
    }

    public onLeaveState(): void {
        this.reset();
    }

    protected onCompleteRoundDelay(): void {

        this._isWait = false;

        if (this.canSpin()) {
            this.proceedWithAutoplay();
        }
    }

    protected reset(): void {
        this._wonBigWin = false;
        this._bigWinFinished = false;
        this._isNoWin = false;
        this._initialWinAnimationFinished = false;
        this._allPayAnimationsFinished = false;
        this._winMeterFinished = false;
    }

    protected setupEvents(): void {
        this.setEventHandler(AutoplayView.STOP_AUTOPLAY_BUTTON_CLICKED, this.goToBasicState.bind(this));
        this.setEventHandler(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, this.onInitialAnimationFinished.bind(this));
        this.setEventHandler(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, this.allPayAnimationsFinished.bind(this));
        this.setEventHandler('General.WinPresentation.WIN_METER_FINISHED', this.onWinMeterFinished.bind(this));
        this.setEventHandler(FlowModuleEvents.WON_BIG_WIN, this.onWonBigWin.bind(this));
        this.setEventHandler(BigWinEvents.FINISHED, this.onBigWinFinished.bind(this));
        this.setEventHandler(FlowModuleEvents.NO_WIN, this.onNoWin.bind(this));
        this.setEventHandler(FlowModuleEvents.CAN_SPIN, this.onCanSpin.bind(this));
        this.setEventHandler(PickBonusEvents.STARTED, this.onBonusGameStarted.bind(this));
        this.setEventHandler(ReelsEvents.REELS_SPINNING_STARTED, this.reset.bind(this));
        this.setEventHandler(RealityCheckEvents.SHOWED, this.goToBasicState.bind(this));
        this.setEventHandler(LogicEvents.REALITY_CHECK, this.goToBasicState.bind(this));
        this.setEventHandler(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted.bind(this));
        this.setEventHandler('Autoplay.STOP', this.goToBasicState.bind(this));
    }

    protected onPopupStarted(type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO || type === GenericPopupType.RESPIN_INTRO) {
            this.onBonusGameStarted();
        }
    }

    protected onCanSpin(): void {
        if (this.canSpin()) {
            this.proceedWithAutoplay();
        }
    }

    /**
     * can spin if all pays are played or skipAutoplayWinAnimation setting is on and initial flow animation is played
     *
     * @returns {boolean}
     */
    protected canSpin(): boolean {
        const skipWinAnimation = this.module.settings.get('autoplaySkipWinAnimation');
        const skipBigWinAnimation = this.module.settings.get('autoplaySkipBigWins');
        const isBigWin = this.module.winPresentationUtils.isBigWin();

        this.module.isDelayBeforeSpin = this._isNoWin || isBigWin || this.module.roundDelay > 0;

        if (this._isWait) {
            return false;
        } else if (isBigWin && !skipBigWinAnimation) {
            return this._bigWinFinished;
        } else {
            return this._isNoWin || skipWinAnimation && this._initialWinAnimationFinished ||
                this._initialWinAnimationFinished && this._winMeterFinished;
        }
    }

    protected onLastPromotionSpinEnded(): void {
        this.module.setSpins(0);
        this.goToBasicState();
    }

    protected onBonusGameStarted(): void {
        this.module.gotoBasicStateBeforeBonusGame();
    }

    protected goToBasicState(): void {
        this.module.gotoState(AutoplayModule.BASIC_STATE);
    }

    protected proceedWithAutoplay(): void {
        if (this.module.currentState.name === this.name) {
            this.module.gotoState(AutoplayModule.READY_FOR_AUTOPLAY_STATE);
        }
    }

    protected onInitialAnimationFinished(): void {
        this._initialWinAnimationFinished = true;
    }

    protected allPayAnimationsFinished(): void {
        this._allPayAnimationsFinished = true;
    }

    protected onWinMeterFinished(): void {
        this._winMeterFinished = true;
    }

    protected onWonBigWin(): void {
        this._wonBigWin = true;
    }

    protected onBigWinFinished(): void {
        this._bigWinFinished = true;
    }

    protected onNoWin(): void {
        this._isNoWin = true;
    }
}
